import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Shield, Mail, Settings, CheckCircle, AlertCircle, Eye, EyeOff, 
  Save, UserX, ArrowLeft, MessageCircle 
} from 'lucide-react';
import DOMPurify from 'dompurify';
import Navbar from '@/components/Navbar';
import SEO from '@/components/SEO';

interface Preferences {
  general: boolean;
  application_security: boolean;
  devsecops: boolean;
  offensive_security: boolean;
  cloud_security: boolean;
  advisory_services: boolean;
}

interface SubscriberData {
  email: string;
  status: string;
  preferences: Preferences;
  list_ids: number[];
  confirmed_at: string;
}

type ActionType = 'preferences' | 'unsubscribe' | 'idle';
type StatusType = 'idle' | 'loading' | 'success' | 'error' | 'not_found' | 'already_unsubscribed';

const PreferenceManagementPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  // Core state
  const [email, setEmail] = useState('');
  const [subscriberData, setSubscriberData] = useState<SubscriberData | null>(null);
  const [preferences, setPreferences] = useState<Preferences>({
    general: true,
    application_security: false,
    devsecops: false,
    offensive_security: false,
    cloud_security: false,
    advisory_services: false
  });
  
  // UI state
  const [status, setStatus] = useState<StatusType>('idle');
  const [message, setMessage] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(true);
  const [currentAction, setCurrentAction] = useState<ActionType>('idle');
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);

  const sanitizeInput = (input: string): string => {
    return DOMPurify.sanitize(input.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  };

  const validateEmail = (email: string): boolean => {
    return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email);
  };

  const loadSubscriberData = useCallback(async (emailAddress?: string) => {
    const targetEmail = emailAddress || email;
    const cleanEmail = sanitizeInput(targetEmail);
    
    if (!cleanEmail || !validateEmail(cleanEmail)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setCurrentAction('preferences');

    try {
      const response = await fetch(`/api/newsletter/preferences/${encodeURIComponent(cleanEmail)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriberData(data);
        setPreferences(data.preferences);
        setEmail(cleanEmail);
        setStatus('success');
        setMessage('');
        setShowEmailInput(false);
      } else if (response.status === 404) {
        setStatus('not_found');
        setMessage('Email address not found in our subscriber list');
        setSubscriberData(null);
        setShowEmailInput(true);
      } else {
        const errorData = await response.json();
        setStatus('error');
        setMessage(errorData.error || 'Failed to load subscription data');
        setShowEmailInput(true);
      }
    } catch (error) {
      console.error('Error loading subscriber data:', error);
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
      setShowEmailInput(true);
    } finally {
      setCurrentAction('idle');
    }
  }, [email]);

  const handleTokenAccess = useCallback(async (accessToken: string) => {
    setStatus('loading');
    setMessage('');

    try {
      // First try to validate token and get subscriber email
      const response = await fetch(`/api/newsletter/token/${accessToken}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.email) {
          setEmail(data.email);
          await loadSubscriberData(data.email);
        }
      } else if (response.status === 404) {
        setStatus('error');
        setMessage('Invalid or expired link. Please enter your email address manually.');
        setShowEmailInput(true);
      } else {
        // Fallback to manual email entry
        setStatus('idle');
        setMessage('Please enter your email address to manage your subscription.');
        setShowEmailInput(true);
      }
    } catch (error) {
      console.error('Token access error:', error);
      setStatus('error');
      setMessage('Network error. Please enter your email address manually.');
      setShowEmailInput(true);
    }
  }, [loadSubscriberData]);

  useEffect(() => {
    // Check for email in URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    
    if (emailParam) {
      const decodedEmail = decodeURIComponent(emailParam);
      setEmail(decodedEmail);
      setShowEmailInput(false);
      loadSubscriberData(decodedEmail);
    } else if (token) {
      // Handle token-based access
      setShowEmailInput(false);
      handleTokenAccess(token);
    }
  }, [token, loadSubscriberData, handleTokenAccess]);

  const savePreferences = async () => {
    if (!subscriberData) return;

    setStatus('loading');
    setCurrentAction('preferences');
    setMessage('');

    try {
      const response = await fetch(`/api/newsletter/preferences/${encodeURIComponent(subscriberData.email)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences })
      });

      if (response.ok) {
        setStatus('success');
        setMessage('Your preferences have been updated successfully!');
      } else {
        const errorData = await response.json();
        setStatus('error');
        setMessage(errorData.error || 'Failed to update preferences');
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      setStatus('error');
      setMessage('Network error. Please try again.');
    } finally {
      setCurrentAction('idle');
    }
  };

  const handleUnsubscribe = async () => {
    if (!subscriberData) return;

    setStatus('loading');
    setCurrentAction('unsubscribe');
    setMessage('');
    setShowUnsubscribeConfirm(false);

    try {
      // Try token-based unsubscribe first if we have a token
      let response;
      if (token) {
        response = await fetch(`/api/newsletter/unsubscribe/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        // Fallback to email-based unsubscribe
        response = await fetch('/api/newsletter/unsubscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: subscriberData.email })
        });
      }

      if (response.ok) {
        setStatus('success');
        setMessage('You have been successfully unsubscribed from all CIPHER Security newsletters.');
        setSubscriberData(null);
        setShowEmailInput(false);
      } else if (response.status === 404) {
        setStatus('not_found');
        setMessage('Subscription not found or already unsubscribed.');
      } else {
        const errorData = await response.json();
        if (errorData.error && errorData.error.includes('already unsubscribed')) {
          setStatus('already_unsubscribed');
          setMessage('This email address is already unsubscribed from our newsletters.');
        } else {
          setStatus('error');
          setMessage(errorData.error || 'Failed to unsubscribe. Please try again.');
        }
      }
    } catch (error) {
      console.error('Unsubscribe error:', error);
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    } finally {
      setCurrentAction('idle');
    }
  };

  const handlePreferenceChange = (key: keyof Preferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const preferenceOptions = [
    {
      key: 'general' as keyof Preferences,
      title: '🛡️ General Security',
      description: 'Weekly cybersecurity insights, industry news, and security best practices'
    },
    {
      key: 'application_security' as keyof Preferences,
      title: '🔒 Application Security',
      description: 'Secure coding practices, SAST/DAST tools, and application vulnerability assessments'
    },
    {
      key: 'devsecops' as keyof Preferences,
      title: '⚡ DevSecOps',
      description: 'Security automation, CI/CD pipeline protection, and shift-left strategies'
    },
    {
      key: 'offensive_security' as keyof Preferences,
      title: '🔴 Offensive Security',
      description: 'Penetration testing methodologies, red team tactics, and vulnerability research'
    },
    {
      key: 'cloud_security' as keyof Preferences,
      title: '☁️ Cloud Security',
      description: 'AWS/Azure/GCP security, compliance frameworks, and cloud architecture protection'
    },
    {
      key: 'advisory_services' as keyof Preferences,
      title: '💼 Advisory Services',
      description: 'Strategic security consulting, compliance guidance, and executive briefings'
    }
  ];

  const isLoading = status === 'loading';
  const hasSubscriberData = subscriberData !== null;
  const isUnsubscribed = status === 'success' && currentAction === 'unsubscribe';

  return (
    <>
      <SEO
        title="Subscription Management - CIPHER Security"
        description="Manage your CIPHER Security newsletter subscription preferences and unsubscribe options. Customize your cybersecurity content or opt out completely."
        keywords={[
          'subscription management',
          'newsletter preferences',
          'unsubscribe',
          'email preferences',
          'cybersecurity newsletter',
          'CIPHER Security'
        ]}
      />
      
      <Navbar />
      
      <div className="relative min-h-screen bg-[#0f0f17] overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f17] via-[#0a0a12] to-[#0f0f17]" />
          
          {/* Orbs */}
          <div 
            className="absolute top-32 -right-48 w-[400px] h-[400px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle at 30% 30%, rgba(0, 168, 107, 0.12) 0%, rgba(6, 182, 212, 0.08) 50%, transparent 70%)',
              filter: 'blur(60px)',
            }}
          />
          <div 
            className="absolute bottom-32 -left-48 w-[350px] h-[350px] rounded-full opacity-15"
            style={{
              background: 'radial-gradient(circle at 70% 70%, rgba(251, 191, 36, 0.10) 0%, rgba(251, 146, 60, 0.06) 50%, transparent 80%)',
              filter: 'blur(80px)',
            }}
          />
        </div>

        <section className="relative z-10 py-20 sm:py-24 lg:py-32">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Header */}
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-20 h-20 bg-gradient-to-r from-[#00a86b] to-[#06b6d4] rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <Settings className="w-10 h-10 text-white" />
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl sm:text-5xl font-bold text-white mb-4"
              >
                Subscription Management
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-lg text-gray-300 max-w-2xl mx-auto"
              >
                Manage your CIPHER Security newsletter preferences or unsubscribe from our mailing list
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-[#1a1a2e]/60 to-[#16213e]/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8"
            >

              {/* Loading State */}
              {isLoading && (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-white">
                    {currentAction === 'preferences' && 'Loading your subscription...'}
                    {currentAction === 'unsubscribe' && 'Processing unsubscribe request...'}
                    {currentAction === 'idle' && 'Processing...'}
                  </p>
                </div>
              )}

              {/* Email Input Section */}
              {showEmailInput && !isLoading && !isUnsubscribed && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white mb-4">Access Your Subscription</h2>
                  <p className="text-gray-300 mb-6">Enter your email address to manage your newsletter preferences</p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && loadSubscriberData()}
                        placeholder="Enter your email address"
                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => loadSubscriberData()}
                      disabled={isLoading}
                      className="px-6 py-3 bg-gradient-to-r from-[#00a86b] to-[#06b6d4] hover:from-[#00c77d] hover:to-[#0891b2] text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Load Subscription
                    </button>
                  </div>
                </div>
              )}

              {/* Status Messages */}
              {status === 'success' && message && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 flex items-center p-4 bg-green-500/20 border border-green-500/40 rounded-lg text-green-200"
                >
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>{message}</span>
                </motion.div>
              )}

              {status === 'error' && message && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 flex items-center p-4 bg-red-500/20 border border-red-500/40 rounded-lg text-red-200"
                >
                  <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>{message}</span>
                </motion.div>
              )}

              {status === 'not_found' && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-6 bg-orange-500/20 border border-orange-500/40 rounded-lg"
                >
                  <div className="flex items-center mb-3">
                    <AlertCircle className="w-5 h-5 mr-3 text-orange-300" />
                    <h3 className="text-orange-200 font-semibold">Email Not Found</h3>
                  </div>
                  <p className="text-orange-200 text-sm mb-4">{message}</p>
                  <div className="text-orange-200 text-sm">
                    <p>Would you like to subscribe to our newsletter?</p>
                    <a 
                      href="/#newsletter-signup" 
                      className="text-[#06b6d4] hover:underline font-semibold"
                    >
                      Subscribe here →
                    </a>
                  </div>
                </motion.div>
              )}

              {status === 'already_unsubscribed' && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-8 h-8 text-orange-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-4">Already Unsubscribed</h2>
                  <p className="text-gray-300 mb-6">{message}</p>
                  
                  <div className="space-y-4">
                    <p className="text-gray-300 text-sm">
                      Want to subscribe again?
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button
                        onClick={() => navigate('/#newsletter-signup')}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#00a86b] to-[#06b6d4] hover:from-[#00c77d] hover:to-[#0891b2] text-white font-semibold rounded-lg transition-all duration-300"
                      >
                        <Mail className="w-4 h-4" />
                        Subscribe Again
                      </button>
                      <button
                        onClick={() => navigate('/')}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 border border-white/20 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-300"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Website
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Unsubscribe Success State */}
              {isUnsubscribed && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-4">Successfully Unsubscribed</h2>
                  <p className="text-gray-300 mb-2">{message}</p>
                  {email && (
                    <p className="text-gray-400 text-sm mb-6">
                      Email: {email}
                    </p>
                  )}
                  
                  <div className="space-y-4">
                    <p className="text-gray-300 text-sm">
                      Changed your mind? You can always subscribe again.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button
                        onClick={() => navigate('/#newsletter-signup')}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#00a86b] to-[#06b6d4] hover:from-[#00c77d] hover:to-[#0891b2] text-white font-semibold rounded-lg transition-all duration-300"
                      >
                        <Mail className="w-4 h-4" />
                        Subscribe Again
                      </button>
                      <button
                        onClick={() => navigate('/')}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 border border-white/20 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-300"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Website
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Main Subscription Management Interface */}
              {hasSubscriberData && !isLoading && !isUnsubscribed && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  {/* Subscriber Information */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2">Subscription Details</h2>
                      <div className="flex items-center text-gray-300">
                        <Mail className="w-4 h-4 mr-2" />
                        <span>{subscriberData.email}</span>
                        <button
                          onClick={() => setShowEmailInput(true)}
                          className="ml-3 text-gray-400 hover:text-white transition-colors"
                          title="Change email"
                        >
                          <EyeOff className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">
                        Subscribed: {new Date(subscriberData.confirmed_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center px-3 py-1 bg-green-500/20 border border-green-500/40 rounded-full text-green-200 text-sm">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Active
                    </div>
                  </div>

                  {/* Preferences Section */}
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Content Preferences</h3>
                    <p className="text-gray-300 mb-6">Choose the types of cybersecurity content you'd like to receive:</p>
                    
                    <div className="space-y-4">
                      {preferenceOptions.map((option) => (
                        <div
                          key={option.key}
                          className="p-4 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
                        >
                          <label className="flex items-start cursor-pointer">
                            <input
                              type="checkbox"
                              checked={preferences[option.key]}
                              onChange={() => handlePreferenceChange(option.key)}
                              className="mr-4 mt-1 w-5 h-5 accent-[#00a86b]"
                            />
                            <div className="flex-1">
                              <h4 className="text-white font-semibold mb-1">{option.title}</h4>
                              <p className="text-gray-400 text-sm">{option.description}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Unsubscribe Confirmation Modal */}
                  {showUnsubscribeConfirm && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg"
                    >
                      <div className="flex items-center mb-4">
                        <UserX className="w-6 h-6 mr-3 text-red-400" />
                        <h3 className="text-lg font-bold text-white">Confirm Unsubscribe</h3>
                      </div>
                      <p className="text-gray-300 mb-6">
                        Are you sure you want to unsubscribe from all CIPHER Security newsletters? 
                        This action will remove you from all our mailing lists.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={handleUnsubscribe}
                          className="flex-1 py-3 px-6 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center"
                        >
                          <UserX className="w-4 h-4 mr-2" />
                          Yes, Unsubscribe
                        </button>
                        <button
                          onClick={() => setShowUnsubscribeConfirm(false)}
                          className="flex-1 py-3 px-6 bg-white/10 border border-white/20 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Action Buttons */}
                  {!showUnsubscribeConfirm && (
                    <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-white/10">
                      <button
                        onClick={savePreferences}
                        disabled={isLoading}
                        className="flex-1 py-3 px-6 bg-gradient-to-r from-[#00a86b] to-[#06b6d4] hover:from-[#00c77d] hover:to-[#0891b2] text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Preferences
                      </button>

                      <button
                        onClick={() => setShowUnsubscribeConfirm(true)}
                        disabled={isLoading}
                        className="py-3 px-6 bg-red-600/20 border border-red-500/40 hover:bg-red-600/30 text-red-200 font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        <UserX className="w-4 h-4 mr-2" />
                        Unsubscribe
                      </button>
                    </div>
                  )}

                  {/* Quick Actions Note */}
                  <div className="text-center pt-4 border-t border-white/10">
                    <p className="text-gray-400 text-sm mb-2">
                      <MessageCircle className="w-4 h-4 inline mr-2" />
                      Need to pause emails temporarily instead of unsubscribing completely?
                    </p>
                    <p className="text-gray-400 text-sm">
                      Simply uncheck all preference categories above and save your settings.
                    </p>
                  </div>
                </motion.div>
              )}

            </motion.div>

            {/* Help Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8 text-center"
            >
              <p className="text-gray-400 text-sm">
                Need help? Contact us at{' '}
                <a href="mailto:contact@ciphersecurity.io" className="text-[#06b6d4] hover:underline">
                  contact@ciphersecurity.io
                </a>
              </p>
            </motion.div>

          </div>
        </section>
      </div>
    </>
  );
};

export default PreferenceManagementPage;